import React, { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import Modal from 'components/Modal'
import ModalButton from 'components/Modal/Button'
import Textarea from 'components/Textarea'
import Spinner from 'components/Spinner'

import SelectCustomer from 'pages/UnassignedDevice/SelectCustomer'

import useCustomersPiiInfo from 'hooks/useCustomersPiiInfo'
import { useServerErrorContext } from 'hooks/ServerErrorContext'

import { scrubText } from 'helpers/inputCleaners'

import { color } from 'styles/colors'
import { ButtonContainer } from 'styles/shared'

import Api from 'services/api'

import { CustomerBase } from 'types/Customer'

import { useConfirmDialog } from 'hooks/useDialog'

import { CONFIRMATION_DIALOG_MESSAGES } from 'helpers/confirmationDialogMessages'

type Props = {
  dealerId: number
  deviceId: string
  closeCallback: () => void
}

const defaultDevice = {
  isNew: true,
  deviceType: 'thermostat',
  deviceId: '',
  note: null
}

const AssignDeviceModal = ({ dealerId, deviceId, closeCallback } : Props) => {
  const navigate = useNavigate()
  const { setServerError } = useServerErrorContext()

  const { customers, areCustomersLoaded } = useCustomersPiiInfo()
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerBase | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const { confirm } = useConfirmDialog()

  const onCancel = async () => {
    if (selectedCustomer || note) {
      const isConfirmed = await confirm({
        title: CONFIRMATION_DIALOG_MESSAGES.CANCEL.TITLE,
        description: CONFIRMATION_DIALOG_MESSAGES.CANCEL.DESCRIPTION,
        confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.OK
      })
      if (isConfirmed) {
        closeCallback()
      }
    } else {
      closeCallback()
    }
  }

  const onSave = useCallback(async () => {
    if (!selectedCustomer) {
      return
    }

    setIsSaving(true)

    const modifiedDevice = { ...defaultDevice, ...{ deviceId, note, dealerId, customerId: selectedCustomer.id } }

    await Api.Device.create(modifiedDevice)
      .then(() => {
        navigate(`/customers/${selectedCustomer.id}/systems/${deviceId}`)
      })
      .catch(() => {
        setIsSaving(false)
        setServerError('Failed to assign UnassignDevice to customer')
      })
  }, [dealerId, deviceId, navigate, note, selectedCustomer, setServerError])

  const onSelectCustomerCallback = useCallback((newSelectedCustomer: CustomerBase) => {
    setSelectedCustomer(newSelectedCustomer)
  }, [])

  return (
    <Modal isDisplayed closeCallback={closeCallback} variant="sm" title="Assign To Customer" >
      {
        !areCustomersLoaded ? <Spinner variant="page" /> : (
          <div>
            <S.Device>{`DEVICE ID: ${deviceId}`}</S.Device>
            <S.Label>CUSTOMER</S.Label>
            <SelectCustomer selectedCustomer={selectedCustomer} onSelectCustomerCallback={onSelectCustomerCallback} customers={customers || []} />
            <S.Label>Notes</S.Label>
            <Textarea role="note" label="" rows={5} value={note || ''} onChange={(event) => setNote(scrubText(event.target.value))} />
            <ButtonContainer noBorder>
              <ModalButton decoration="secondary" styles={{ button: { marginRight: 15 } }} text="Cancel" onPress={onCancel} />
              <ModalButton decoration="positive" enabled={!isSaving} text="Save" onPress={onSave} />
            </ButtonContainer>
          </div>
        )
      }
    </Modal>
  )
}

export default AssignDeviceModal

const S = {
  Device: styled.h2`
    padding-bottom: 10px;
    border-bottom: 1px solid ${color('border')};
  `,
  Label: styled.h2`
    padding-top: 10px;
  `
}
